import React, { useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createLinkNode } from '@lexical/link';
import { $createTextNode, $getSelection, $isRangeSelection } from 'lexical';
import { useLinkDialog } from './lib/linkDialog';
import './LinkPopover.css';

/**
 * composer-v2 LinkPopover — link entry UI. KAP-304 + post-user-smoke
 * addition of the Text field so the popover can edit both the visible
 * link text and the URL (Slack's "Add link" modal).
 *
 * Behavior:
 *   - If text was selected when opened, `defaultText` pre-populates the
 *     Text field. The user can keep, edit, or extend it.
 *   - If no text was selected, the Text field is empty and the Save
 *     button requires both Text + URL.
 *   - On Save: build a LinkNode containing a TextNode (the visible
 *     text) and insertNodes() at the selection. This replaces the
 *     original selection cleanly without the selection-juggling that
 *     the previous URL-only TOGGLE_LINK_COMMAND path required.
 *   - Esc, Cancel, and click-outside all cancel.
 *   - Returns focus to the editor.
 */

function normalizeUrl(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function LinkPopover() {
  const [editor] = useLexicalComposerContext();
  const { isOpen, anchorRect, defaultUrl, defaultText, close } = useLinkDialog();
  const [draftText, setDraftText] = useState('');
  const [draftUrl, setDraftUrl] = useState('');
  const textInputRef = useRef(null);
  const urlInputRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDraftText(defaultText || '');
      setDraftUrl(defaultUrl || '');
      // Focus the URL input when text was pre-populated (the common case
      // — user selected text first, then opened the dialog) so they can
      // type the URL immediately. If no text was selected, focus the
      // Text field first so they can fill it in.
      const id = window.setTimeout(() => {
        const target = defaultText ? urlInputRef.current : textInputRef.current;
        if (target) {
          target.focus();
          target.select();
        }
      }, 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [isOpen, defaultUrl, defaultText]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const cancel = () => {
    close();
    editor.focus();
  };

  const apply = () => {
    const url = normalizeUrl(draftUrl);
    if (!url || url === 'https://') {
      cancel();
      return;
    }
    const text = draftText.trim() || url;

    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const linkNode = $createLinkNode(url);
      linkNode.append($createTextNode(text));
      selection.insertNodes([linkNode]);
    });

    close();
    editor.focus();
  };

  const style = anchorRect
    ? {
        position: 'fixed',
        top: Math.max(8, anchorRect.top - 168),
        left: Math.max(8, anchorRect.left)
      }
    : { position: 'fixed', top: 80, left: 80 };

  const canSave = draftUrl.trim().length > 0 && draftText.trim().length > 0;

  return (
    <div
      ref={popoverRef}
      className="composer-v2-link-popover"
      role="dialog"
      aria-label="Add link"
      style={style}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
    >
      <div className="composer-v2-link-popover-header">
        <span className="composer-v2-link-popover-title">Add link</span>
        <button
          type="button"
          className="composer-v2-link-popover-close"
          onClick={cancel}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="composer-v2-link-popover-field">
        <label htmlFor="composer-v2-link-popover-text" className="composer-v2-link-popover-label">
          Text
        </label>
        <input
          id="composer-v2-link-popover-text"
          ref={textInputRef}
          type="text"
          className="composer-v2-link-popover-input"
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (urlInputRef.current) urlInputRef.current.focus();
            }
          }}
          placeholder="Text to display"
          aria-label="Link text"
        />
      </div>

      <div className="composer-v2-link-popover-field">
        <label htmlFor="composer-v2-link-popover-url" className="composer-v2-link-popover-label">
          Link
        </label>
        <input
          id="composer-v2-link-popover-url"
          ref={urlInputRef}
          type="url"
          className="composer-v2-link-popover-input"
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              apply();
            }
          }}
          aria-label="Link URL"
          placeholder="https://example.com"
        />
      </div>

      <div className="composer-v2-link-popover-actions">
        <button
          type="button"
          className="composer-v2-link-popover-cancel"
          onClick={cancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="composer-v2-link-popover-apply"
          onClick={apply}
          disabled={!canSave}
        >
          Save
        </button>
      </div>
    </div>
  );
}

export default LinkPopover;
